import { LucideIcon } from 'lucide-react';
import { MiniSparkline } from './MiniSparkline';
import { DeltaBadge } from './DeltaBadge';

interface SummaryCardProps {
    title: string;
    value: string | number;
    icon: LucideIcon;
    iconColor: string;
    bgGradient?: string;
    subtitle?: string;
    subItems?: Array<{ label: string; value: string | number }>;
    /** Período do dado (ex.: "8D") exibido ao lado do rótulo. */
    period?: string;
    /** Série para a mini-sparkline (largura total, abaixo do número). */
    spark?: number[];
    /** Variação já com sinal (ex.: "+12,4%"). */
    delta?: { value: string; positive: boolean; title?: string };
    /** Nota ao lado da variação (ex.: "vs ant."). */
    deltaNote?: string;
    /** Quando true, sem borda/arredondamento próprios (encaixe em grade hairline). */
    flush?: boolean;
    /** Cor do grupo temático — classe de borda superior (ex.: "border-t-cyan-500/50"). */
    accent?: string;
    /** Número menor (linha 2, cards mais estreitos). */
    compact?: boolean;
}

/**
 * Card de métrica — estilo terminal (mockup): rótulo mono "NN // TÍTULO",
 * número grande tabular, sparkline de largura total e chip de variação.
 */
export function SummaryCard({
    title,
    value,
    icon: Icon,
    iconColor,
    subtitle,
    subItems,
    spark,
    delta,
    deltaNote,
    flush,
    accent,
    compact,
    period,
}: SummaryCardProps) {
    const hasSpark = !!spark && spark.length > 1;

    return (
        <div
            className={`group relative flex flex-col ${
                flush ? 'bg-card' : 'rounded-lg border border-border bg-card'
            } ${accent ? `border-t-2 ${accent}` : ''} p-4 transition-colors`}
        >
            {/* Rótulo */}
            <div className="flex items-center justify-between gap-2">
                <p className="truncate text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    {title}
                    {period && <span className="ml-1.5 text-foreground/30">· {period}</span>}
                </p>
                <Icon className={`h-3.5 w-3.5 shrink-0 ${iconColor || 'text-muted-foreground'}`} />
            </div>

            {/* Número (herda a cor sutil do card) */}
            <p
                className={`mt-2 ${
                    compact ? 'text-xl' : 'text-[1.65rem]'
                } truncate font-semibold leading-none tabular-nums ${iconColor || 'text-foreground'}`}
            >
                {value}
            </p>

            {/* Sparkline (largura total) */}
            {hasSpark && (
                <div className="mt-3 h-8 w-full text-foreground/35">
                    <MiniSparkline values={spark!} height={32} />
                </div>
            )}

            {/* Variação + info secundária */}
            {(delta || subtitle || deltaNote || (subItems && subItems.length > 0)) && (
                <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                    {delta && <DeltaBadge value={delta.value} positive={delta.positive} title={delta.title} />}
                    {deltaNote && <span className="text-muted-foreground">{deltaNote}</span>}
                    {subtitle && <span className="text-muted-foreground">{subtitle}</span>}
                    {subItems &&
                        subItems.map((item, idx) =>
                            item.value === '' ? (
                                <span key={idx} className="text-muted-foreground">{item.label}</span>
                            ) : (
                                <span key={idx} className="text-muted-foreground">
                                    {item.label}: <span className="tabular-nums text-foreground/90">{item.value}</span>
                                </span>
                            )
                        )}
                </div>
            )}
        </div>
    );
}
